import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, FileSignature, Loader2, Send, CheckCircle2, Clock, XCircle, ExternalLink } from "lucide-react";
import { API_BASE, formatApiError } from "./AuthContext";
import { useDocusealTemplates } from "../hooks/useDocusealTemplates";

/**
 * v68.16 — Job Stages Drawer
 *
 * Lists all stages of a job with per-stage actions:
 *   - "Send for signature" opens a compact form (provider / template / signer
 *     + prefill editor) and POSTs to `/api/jobs/{job_id}/stages/{idx}/send-signature`.
 *
 * Provider defaults resolve on the backend (stage → firm default → "builtin"),
 * so the frontend just displays what's currently effective and lets the user
 * override per envelope.
 */
export default function JobStagesDrawer({ job, onClose, orgDefaults }) {
  const [activeIdx, setActiveIdx] = useState(null);

  if (!job) return null;
  const stages = job.stages || [];

  return (
    <div className="fixed inset-0 z-40 flex justify-end" data-testid="job-stages-drawer"
         onKeyDown={(e) => e.key === "Escape" && onClose()}>
      <button className="flex-1 bg-black/30" onClick={onClose} aria-label="Close drawer" />
      <aside className="w-full max-w-lg bg-[var(--paper)] shadow-2xl overflow-y-auto"
             style={{ borderLeft: "1px solid var(--border)" }}>
        <div className="px-6 py-5 border-b border-[var(--border)] sticky top-0 bg-[var(--paper)]">
          <div className="flex items-center justify-between">
            <div>
              <div className="overline">Stages</div>
              <div className="font-heading text-lg mt-1 leading-tight" style={{ fontWeight: 500 }}>
                {job.title}
              </div>
            </div>
            <button className="btn-ghost" onClick={onClose} data-testid="job-stages-close">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-3">
          {stages.length === 0 && (
            <div className="text-[13px] text-[var(--ink-3)] py-4">
              This job has no stages. Apply a template on create to gain a stage timeline.
            </div>
          )}
          {stages.map((s, idx) => (
            <StageRow
              key={idx}
              index={idx}
              stage={s}
              job={job}
              expanded={activeIdx === idx}
              onToggle={() => setActiveIdx(activeIdx === idx ? null : idx)}
              orgDefaults={orgDefaults}
            />
          ))}
        </div>
      </aside>
    </div>
  );
}

function StageRow({ stage, index, job, expanded, onToggle, orgDefaults }) {
  const isClient = !!stage.is_client_request;
  const hasCfg = !!stage.esign_config;
  const effectiveProvider = stage?.esign_config?.provider
    || orgDefaults?.default_esign_provider
    || "builtin";
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);      // bump to refetch after send

  useEffect(() => {
    let cancel = false;
    setHistoryLoading(true);
    axios.get(`${API_BASE}/jobs/${job.job_id}/stages/${index}/envelopes`)
      .then((r) => { if (!cancel) setHistory(r.data.envelopes || []); })
      .catch(() => { if (!cancel) setHistory([]); })
      .finally(() => { if (!cancel) setHistoryLoading(false); });
    return () => { cancel = true; };
  }, [job.job_id, index, historyKey]);

  return (
    <div className="border border-[var(--border)] rounded-md" data-testid={`stage-row-${index}`}>
      <div className="p-4 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[11px] text-[var(--ink-3)]">{String(index + 1).padStart(2, "0")}</span>
            <div className="font-medium text-[14px] text-[var(--ink)] truncate">{stage.name}</div>
          </div>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            {stage.section && <span className="badge badge-outline text-[9px]">{stage.section}</span>}
            {stage.target_status && (
              <span className="badge text-[9px]" style={{ background: "var(--ink-2)", color: "var(--paper)" }}>
                → {stage.target_status}
              </span>
            )}
            {isClient && <span className="badge badge-outline text-[9px]">client</span>}
            {hasCfg && <span className="badge badge-outline text-[9px]">esign configured</span>}
            {history.length > 0 && (
              <span className="badge badge-outline text-[9px]" data-testid={`stage-envelope-count-${index}`}>
                {history.length} envelope{history.length === 1 ? "" : "s"}
              </span>
            )}
          </div>
          {stage.client_prompt && (
            <p className="text-[12px] text-[var(--ink-2)] mt-2 leading-relaxed">{stage.client_prompt}</p>
          )}
        </div>
        <button
          className="btn-secondary shrink-0 text-[12px]"
          onClick={onToggle}
          data-testid={`stage-send-sig-btn-${index}`}
        >
          <FileSignature size={13} /> {expanded ? "Cancel" : "Send for signature"}
        </button>
      </div>
      {(history.length > 0 || historyLoading) && (
        <div className="px-4 pb-3">
          <EnvelopeTimeline entries={history} loading={historyLoading} stageIndex={index} />
        </div>
      )}
      {expanded && (
        <div className="border-t border-[var(--border)] bg-[var(--bg)] p-4">
          <SignatureForm
            job={job}
            stageIndex={index}
            defaultProvider={effectiveProvider}
            orgDefaults={orgDefaults}
            onSent={() => { onToggle(); setHistoryKey((k) => k + 1); }}
          />
        </div>
      )}
    </div>
  );
}

function EnvelopeTimeline({ entries, loading, stageIndex }) {
  if (loading && entries.length === 0) {
    return <div className="text-[11px] text-[var(--ink-3)] flex items-center gap-1.5"><Loader2 size={10} className="animate-spin" /> Loading history…</div>;
  }
  return (
    <ol className="space-y-2 border-l border-[var(--border)] pl-3 ml-1 mt-2" data-testid={`envelope-timeline-${stageIndex}`}>
      {entries.map((e) => <EnvelopeRow key={`${e.provider}-${e.id}`} e={e} />)}
    </ol>
  );
}

function EnvelopeRow({ e }) {
  const isFinal = e.status === "signed" || e.status === "completed";
  const isDeclined = e.status === "declined" || e.status === "expired";
  const Icon = isFinal ? CheckCircle2 : isDeclined ? XCircle : Clock;
  const color = isFinal ? "text-[var(--moss)]" : isDeclined ? "text-[var(--accent)]" : "text-[var(--ink-3)]";
  const when = e.completed_at || e.created_at;
  return (
    <li className="text-[11.5px] flex items-start gap-2 -ml-[7px]" data-testid={`envelope-entry-${e.id}`}>
      <span className={`shrink-0 mt-0.5 rounded-full bg-[var(--paper)] p-[1.5px] ${color}`}>
        <Icon size={11} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`badge text-[9px] uppercase tracking-wider ${isFinal ? "text-[var(--moss)]" : isDeclined ? "text-[var(--accent)]" : "text-[var(--ink-2)]"}`}>
            {e.status}
          </span>
          <span className="text-[var(--ink-3)] uppercase text-[9px] tracking-wider">{e.provider}</span>
          {when && <span className="text-[var(--ink-3)]">· {new Date(when).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>}
        </div>
        <div className="text-[var(--ink)] leading-tight mt-0.5 truncate">{e.title || e.signer_email}</div>
        {(e.audit_url || e.embed_url) && (
          <a href={e.audit_url || e.embed_url} target="_blank" rel="noreferrer"
             className="text-[10px] text-[var(--ink-3)] hover:text-[var(--ink)] inline-flex items-center gap-1 mt-0.5">
            <ExternalLink size={9} /> {e.audit_url ? "audit" : "open"}
          </a>
        )}
      </div>
    </li>
  );
}

function SignatureForm({ job, stageIndex, defaultProvider, orgDefaults, onSent }) {
  const [provider, setProvider] = useState(defaultProvider);
  const [signerEmail, setSignerEmail] = useState("");
  const [signerName, setSignerName] = useState("");
  const [title, setTitle] = useState("");
  const [templateId, setTemplateId] = useState(orgDefaults?.default_docuseal_template_id || "");
  const [prefill, setPrefill] = useState({});
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const dsTemplates = useDocusealTemplates(provider === "docuseal");

  // Load prefill suggestions on mount
  useEffect(() => {
    let cancel = false;
    const run = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${API_BASE}/jobs/${job.job_id}/stages/${stageIndex}/prefill-suggest`,
        );
        if (cancel) return;
        setPrefill(data.suggested || {});
        setSignerEmail(data.signer_email_default || "");
        setSignerName(data.signer_name_default || "");
        setTitle(`${job.stages[stageIndex]?.name || "Signature"} — ${job.title || ""}`.trim());
      } catch (err) {
        if (!cancel) toast.error(formatApiError(err));
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    run();
    return () => { cancel = true; };
  }, [job.job_id, stageIndex, job.title, job.stages]);

  const prefillEntries = useMemo(() => Object.entries(prefill), [prefill]);
  const updatePrefill = (k, v) => setPrefill((p) => ({ ...p, [k]: v }));

  const onSend = async () => {
    if (!signerEmail) { toast.error("Signer email is required"); return; }
    if (provider === "docuseal" && !templateId) { toast.error("DocuSeal requires a template_id"); return; }
    setSending(true);
    try {
      const body = {
        provider,
        signer_email: signerEmail,
        signer_name: signerName || undefined,
        title: title || undefined,
        prefill: Object.fromEntries(Object.entries(prefill).filter(([, v]) => v)),
      };
      if (provider === "docuseal") body.template_id = Number(templateId);
      const { data } = await axios.post(
        `${API_BASE}/jobs/${job.job_id}/stages/${stageIndex}/send-signature`, body,
      );
      toast.success(`Signature request sent via ${data.provider}.`);
      onSent();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSending(false); }
  };

  if (loading) {
    return <div className="text-[12px] text-[var(--ink-3)] flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading prefill…</div>;
  }

  return (
    <div className="space-y-3" data-testid={`sig-form-${stageIndex}`}>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="overline block mb-1">Provider</label>
          <select className="w-full border border-[var(--border)] rounded px-2 py-1.5 text-[13px] bg-white"
                  value={provider} onChange={(e) => setProvider(e.target.value)}
                  data-testid={`sig-provider-${stageIndex}`}>
            <option value="builtin">Built-in (typed signature)</option>
            <option value="docuseal">DocuSeal</option>
          </select>
        </div>
        {provider === "docuseal" && (
          <div>
            <label className="overline block mb-1">DocuSeal Template</label>
            {dsTemplates.error ? (
              <input type="number" className="w-full border border-[var(--border)] rounded px-2 py-1.5 text-[13px] bg-white"
                     value={templateId} onChange={(e) => setTemplateId(e.target.value)}
                     placeholder="Template ID (DocuSeal offline)"
                     data-testid={`sig-tpl-${stageIndex}`} />
            ) : dsTemplates.loading ? (
              <div className="w-full border border-[var(--border)] rounded px-2 py-1.5 text-[13px] bg-white text-[var(--ink-3)] flex items-center gap-2">
                <Loader2 size={12} className="animate-spin" /> Loading templates…
              </div>
            ) : (
              <select className="w-full border border-[var(--border)] rounded px-2 py-1.5 text-[13px] bg-white"
                      value={templateId} onChange={(e) => setTemplateId(e.target.value)}
                      data-testid={`sig-tpl-${stageIndex}`}>
                <option value="">Select a template…</option>
                {dsTemplates.templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            )}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="overline block mb-1">Signer email</label>
          <input className="w-full border border-[var(--border)] rounded px-2 py-1.5 text-[13px] bg-white"
                 value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)}
                 data-testid={`sig-email-${stageIndex}`} />
        </div>
        <div>
          <label className="overline block mb-1">Signer name</label>
          <input className="w-full border border-[var(--border)] rounded px-2 py-1.5 text-[13px] bg-white"
                 value={signerName} onChange={(e) => setSignerName(e.target.value)}
                 data-testid={`sig-name-${stageIndex}`} />
        </div>
      </div>
      <div>
        <label className="overline block mb-1">Envelope title</label>
        <input className="w-full border border-[var(--border)] rounded px-2 py-1.5 text-[13px] bg-white"
               value={title} onChange={(e) => setTitle(e.target.value)}
               data-testid={`sig-title-${stageIndex}`} />
      </div>
      <details className="border border-[var(--border)] rounded bg-white" open>
        <summary className="px-3 py-2 cursor-pointer text-[12px] text-[var(--ink-2)] font-medium">
          Auto-mapped fields ({prefillEntries.filter(([, v]) => v).length}/{prefillEntries.length})
        </summary>
        <div className="p-3 space-y-2 border-t border-[var(--border)]">
          {prefillEntries.map(([k, v]) => (
            <div key={k} className="flex items-center gap-2">
              <label className="text-[11px] text-[var(--ink-3)] font-mono w-32 shrink-0 truncate">{k}</label>
              <input className="flex-1 border border-[var(--border)] rounded px-2 py-1 text-[12px] bg-white"
                     value={v || ""} onChange={(e) => updatePrefill(k, e.target.value)}
                     data-testid={`sig-prefill-${k}`} />
            </div>
          ))}
        </div>
      </details>
      <div className="flex items-center justify-end gap-2 pt-1">
        <div className="text-[11px] text-[var(--ink-3)] mr-auto flex items-center gap-1.5">
          <CheckCircle2 size={11} className="text-[var(--moss)]" />
          Signature will auto-advance the job to <span className="font-mono">{job.stages[stageIndex]?.target_status}</span>
        </div>
        <button className="btn-primary" onClick={onSend} disabled={sending}
                data-testid={`sig-send-${stageIndex}`}>
          {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send envelope
        </button>
      </div>
    </div>
  );
}
